import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  MapPin,
  User,
  Car,
  Clock,
  IndianRupee,
  Calendar,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../../components/ui/Button/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { createTrip, TRIP_TYPE } from '../../store/tripSlice';

export function CreateTripPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const drivers = useSelector(state => state.drivers?.drivers || []);
  
  const [formData, setFormData] = useState({
    passenger: {
      name: '',
      phone: '',
      email: ''
    },
    pickup: {
      name: '',
      address: '',
      coordinates: { lat: 0, lng: 0 }
    },
    dropoff: {
      name: '',
      address: '',
      coordinates: { lat: 0, lng: 0 }
    },
    type: TRIP_TYPE.STANDARD,
    scheduledAt: '',
    notes: '',
    estimatedDistance: '',
    estimatedDuration: '',
    estimatedFare: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[`${section}.${field}`] || errors[field]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: undefined,
        [field]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Passenger validation
    if (!formData.passenger.name.trim()) {
      newErrors['passenger.name'] = 'Passenger name is required';
    }
    if (!formData.passenger.phone.trim()) {
      newErrors['passenger.phone'] = 'Phone number is required';
    }

    // Pickup validation
    if (!formData.pickup.name.trim()) {
      newErrors['pickup.name'] = 'Pickup location is required';
    }
    if (!formData.pickup.address.trim()) {
      newErrors['pickup.address'] = 'Pickup address is required';
    }

    // Dropoff validation
    if (!formData.dropoff.name.trim()) {
      newErrors['dropoff.name'] = 'Dropoff location is required';
    }
    if (!formData.dropoff.address.trim()) {
      newErrors['dropoff.address'] = 'Dropoff address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Calculate estimated values if not provided
      const distance = formData.estimatedDistance || (Math.random() * 15 + 2).toFixed(1);
      const duration = formData.estimatedDuration || Math.floor(Math.random() * 30 + 10);
      const baseFare = parseFloat(distance) * 2.5 + duration * 0.5;

      const tripData = {
        ...formData,
        distance: parseFloat(distance),
        estimatedDuration: parseInt(duration),
        fare: {
          base: baseFare,
          surge: 1.0,
          total: baseFare,
          currency: 'USD'
        },
        payment: {
          method: 'card',
          status: 'pending'
        }
      };

      dispatch(createTrip(tripData));
      
      // Navigate back to trips overview
      navigate('/trips');
    } catch (error) {
      console.error('Error creating trip:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const InputField = ({ label, value, onChange, error, placeholder, type = 'text', required = false }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          onClick={() => navigate('/trips')}
          className="flex items-center"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Trips
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create New Trip</h1>
          <p className="mt-2 text-gray-600">
            Manually create and assign a new trip request
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Passenger Information */}
            <Card className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <User className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Passenger Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Full Name"
                  value={formData.passenger.name}
                  onChange={(value) => handleInputChange('passenger', 'name', value)}
                  error={errors['passenger.name']}
                  placeholder="Enter passenger name"
                  required
                />
                <InputField
                  label="Phone Number"
                  value={formData.passenger.phone}
                  onChange={(value) => handleInputChange('passenger', 'phone', value)}
                  error={errors['passenger.phone']}
                  placeholder="+1 (555) 123-4567"
                  required
                />
                <div className="md:col-span-2">
                  <InputField
                    label="Email Address"
                    value={formData.passenger.email}
                    onChange={(value) => handleInputChange('passenger', 'email', value)}
                    error={errors['passenger.email']}
                    placeholder="passenger@example.com"
                    type="email"
                  />
                </div>
              </div>
            </Card>

            {/* Trip Route */}
            <Card className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <MapPin className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Trip Route</h2>
              </div>
              <div className="space-y-4">
                {/* Pickup Location */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Pickup Location Name"
                    value={formData.pickup.name}
                    onChange={(value) => handleInputChange('pickup', 'name', value)}
                    error={errors['pickup.name']}
                    placeholder="e.g., Downtown Mall"
                    required
                  />
                  <InputField
                    label="Pickup Address"
                    value={formData.pickup.address}
                    onChange={(value) => handleInputChange('pickup', 'address', value)}
                    error={errors['pickup.address']}
                    placeholder="123 Main St, City, State"
                    required
                  />
                </div>

                {/* Dropoff Location */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Dropoff Location Name"
                    value={formData.dropoff.name}
                    onChange={(value) => handleInputChange('dropoff', 'name', value)}
                    error={errors['dropoff.name']}
                    placeholder="e.g., Airport Terminal"
                    required
                  />
                  <InputField
                    label="Dropoff Address"
                    value={formData.dropoff.address}
                    onChange={(value) => handleInputChange('dropoff', 'address', value)}
                    error={errors['dropoff.address']}
                    placeholder="456 Airport Rd, Terminal 1"
                    required
                  />
                </div>
              </div>
            </Card>

            {/* Trip Details */}
            <Card className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Car className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Trip Details</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trip Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange(null, 'type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={TRIP_TYPE.STANDARD}>Standard</option>
                    <option value={TRIP_TYPE.PREMIUM}>Premium</option>
                    <option value={TRIP_TYPE.SHARED}>Shared</option>
                    <option value={TRIP_TYPE.SCHEDULED}>Scheduled</option>
                  </select>
                </div>
                <InputField
                  label="Scheduled Time (Optional)"
                  value={formData.scheduledAt}
                  onChange={(value) => handleInputChange(null, 'scheduledAt', value)}
                  type="datetime-local"
                />
                <InputField
                  label="Estimated Distance (km)"
                  value={formData.estimatedDistance}
                  onChange={(value) => handleInputChange(null, 'estimatedDistance', value)}
                  placeholder="Auto-calculated if empty"
                  type="number"
                  step="0.1"
                />
                <InputField
                  label="Estimated Duration (min)"
                  value={formData.estimatedDuration}
                  onChange={(value) => handleInputChange(null, 'estimatedDuration', value)}
                  placeholder="Auto-calculated if empty"
                  type="number"
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Special Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange(null, 'notes', e.target.value)}
                  placeholder="Any special instructions or notes for the driver"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Trip Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Trip Type</span>
                  <Badge variant="info">{formData.type}</Badge>
                </div>
                {formData.estimatedDistance && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Distance</span>
                    <span className="font-medium">{formData.estimatedDistance} km</span>
                  </div>
                )}
                {formData.estimatedDuration && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Duration</span>
                    <span className="font-medium">{formData.estimatedDuration} min</span>
                  </div>
                )}
                {formData.estimatedFare && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estimated Fare</span>
                    <span className="font-medium">${formData.estimatedFare}</span>
                  </div>
                )}
              </div>
            </Card>

            <div className="space-y-3">
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Creating Trip...' : 'Create Trip'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/trips')}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
